import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  useIsMobile,
  useLinkTo,
  useInTest,
  useOnce,
  useAssignTest,
} from '../../../../hooks';
import formatProfilePhoto from '../../../../modules/formatters/formatProfilePhoto';
import analytics from '../../../../modules/analytics';
import { DeprecatedProfileImageGrid } from './DeprecatedProfileImageGrid';
import { isEmpty, EmptyableImage } from './types';

import { CLIENT_PROPROFILE_DESKTOP_IMAGE_GRID_TEST, CLIENT_PROPROFILE_DESKTOP_IMAGE_GRID_TEST_PERCENT } from '../constants';
import { ProfileImageSwiperContainer } from '../ProfileImageSwiper';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { useImageGrid } from './useImageGrid';
import { PortalOverlay, PortalOverlayModal } from '../../../ui';
import {
  PROVIDER_VANITY_PHOTO,
  PRO_GALLERY,
  PRO_IMAGE_LIST,
} from '../../../../route-names';
import { PublicProvider } from '../../../../api/Providers';
import { ProfileGalleryImage } from '../../../../api/Providers/GalleryImages';

export type DeprecatedProfileImageGridContainerProps = {
  galleryImages: Array<ProfileGalleryImage>;
  profile: ClientProviderProfile | PublicProvider;
  clickParams?: Record<string, any>;
  isEditMode?: boolean;
  isOwnProfile?: boolean;
  onAddImageClick?: () => void;
  disableBottomPadding?: boolean;
};

const ROW_SIZE = 3;
const MAX_ROWS = 2;

export const DeprecatedProfileImageGridContainer: React.FCWithChildren<
DeprecatedProfileImageGridContainerProps
> = ({
  galleryImages,
  isEditMode,
  isOwnProfile,
  clickParams,
  profile,
  onAddImageClick,
  disableBottomPadding = false,
}) => {
  const linkTo = useLinkTo();
  const isMobile = useIsMobile();

  const [showGalleryModal, setShowGalleryModal] = useState<boolean>(false);
  const [initialGalleryIndex, setInitialGalleryIndex] = useState<number>(-1);
  const inGalleryTest = useInTest(CLIENT_PROPROFILE_DESKTOP_IMAGE_GRID_TEST);

  const assignGalleryTest = useAssignTest(
    CLIENT_PROPROFILE_DESKTOP_IMAGE_GRID_TEST,
    CLIENT_PROPROFILE_DESKTOP_IMAGE_GRID_TEST_PERCENT,
  );

  useOnce(() => {
    if (!isOwnProfile && !isMobile) {
      assignGalleryTest();
    }
  }, !isOwnProfile && !isMobile);

  /** The maximum rows to display in the grid */
  const maxRows = useMemo<number>(() => {
    const imageCount = galleryImages.length;
    const allowedRows = Math.min(MAX_ROWS, Math.ceil(imageCount / ROW_SIZE));

    return isMobile ? 1 : allowedRows;
  }, [
    galleryImages.length,
    isMobile,
  ]);

  const { rows, hasMore } = useImageGrid(maxRows, ROW_SIZE, isEditMode, galleryImages);

  const handleImageClick = useCallback((image: EmptyableImage, index: number) => {
    if (isEmpty(image)) {
      if (isEditMode) {
        onAddImageClick?.();
      }
    } else {
      if (!isOwnProfile) {
        analytics.track('client_proprofile_gallery_click', {
          ...clickParams,
          profile_type: 'provider',
          cover_image_click: false,
          provider_id: profile.id,
          image_id: image.id,
          gallery_image: index,
        });
      }

      /**
       * we want to preserve the experience for pros looking at their own profile, but for clients
       * in the test: if they are clicking on the "see all" image, navigate to the multiple image
       * gallery, otherwise open the single image. Do this whether or not they're on desktop.
       * non-test experience should be the same as existing.
       */
      if (isMobile || (inGalleryTest && !isOwnProfile)) {
        /*
          * opens edit photo gallery when in edit mode
          * and view only gallery for all other mobile users
          */
        linkTo(
          isEditMode ? PRO_IMAGE_LIST : PRO_GALLERY,
          { providerId: image.provider || profile.id },
        );
      } else {
        linkTo(PROVIDER_VANITY_PHOTO, {
          providerId: profile.vanity_url,
          imageId: image.id,
        });
      }
    }
  }, [
    clickParams,
    isEditMode,
    isMobile,
    linkTo,
    onAddImageClick,
    inGalleryTest,
    profile,
    isOwnProfile,
  ]);

  const handleGalleryClose = () => {
    setShowGalleryModal(false);
    setInitialGalleryIndex(-1);
  };

  return (
    <React.Fragment>
      <DeprecatedProfileImageGrid
        imageRows={rows}
        imageRowSize={ROW_SIZE}
        hasMore={hasMore}
        onImageClick={handleImageClick}
        isEditMode={isEditMode}
        formatImageUrl={url => formatProfilePhoto(url, '_200x200.jpg')}
        disableBottomPadding={disableBottomPadding}
      />
      {showGalleryModal && (
        <PortalOverlay
          onBackdropClick={handleGalleryClose}
          name="gallery-image-modal"
        >
          <PortalOverlayModal
            position="fullScreen"
            testID="ProfileImageSwiperContainer"
          >
            <ProfileImageSwiperContainer
              galleryImages={galleryImages}
              initialIndex={initialGalleryIndex}
              onAvatarPress={handleGalleryClose}
              onClose={handleGalleryClose}
              profile={profile}
              isEditMode={isEditMode}
            />
          </PortalOverlayModal>
        </PortalOverlay>
      )}
    </React.Fragment>
  );
};
